import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Radio,
} from "@headlessui/react";
import { useTranslate } from "@lobby/ocb-intl";
import { useEffect, useRef, useState } from "react";

import { Payment, Player, usePlayerMoneyFormatter } from "../../../../../entities";
import { Icon, type PaymentCurrency, isNil } from "../../../../../shared";

import type { FormEvent, RefObject } from "react";

export function BankAccountField() {
  const { $t } = useTranslate();

  return (
    <>
      <span>{$t({ defaultMessage: "Enter bank account number" })}</span>
      <div className="max-w-[28.125rem]">
        <label className="block font-bold">
          {$t({ defaultMessage: "Account number" })}
          {" *"}
        </label>
        <input
          type="text"
          name="payment-account"
          placeholder={$t({ defaultMessage: "Bank account number" })}
          required
          className="input w-full"
        />
      </div>
    </>
  );
}

interface CryptoCurrencyBlockProps {
  methodId: number;
}

export function CryptoCurrencyBlock({ methodId }: CryptoCurrencyBlockProps) {
  const { $t } = useTranslate();
  const [selected, setSelected] = useState<PaymentCurrency | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const playerData = Player.usePlayer();
  const playerCurrency = playerData.data?.currency ?? "";

  const { data: currencyListResponse, isPending } = Payment.useCurrencyList(
    methodId,
    playerCurrency,
  );
  const currencyList = currencyListResponse?.result?.data ?? [];
  const activeCurrency = selected ?? currencyList[0] ?? null;

  function handleChange(value: PaymentCurrency | null) {
    setSelected(value);
    if (inputRef.current) {
      inputRef.current.setCustomValidity(
        value ? "" : $t({ defaultMessage: "Please select a currency" }),
      );
    }
  }

  useEffect(() => {
    if (inputRef.current && !activeCurrency) {
      inputRef.current.setCustomValidity($t({ defaultMessage: "Please select a currency" }));
    } else if (inputRef.current && activeCurrency) {
      inputRef.current.setCustomValidity("");
    }
  }, [activeCurrency, $t]);

  return (
    <div className="billing-information-block">
      <div>
        <label htmlFor="crypt-currency-input">
          {$t({ defaultMessage: "Currency" })}
          {" *"}
        </label>
        <input type="hidden" name="crypt-billing-currency" value={activeCurrency?.name ?? ""} />
        <div className="combobox-wrapper !max-w-full">
          <Combobox<PaymentCurrency>
            immediate
            value={activeCurrency ?? undefined}
            onChange={handleChange}
          >
            <div className="relative">
              <ComboboxInput<PaymentCurrency>
                ref={inputRef}
                id="crypt-currency-input"
                className="combobox--input"
                placeholder={$t({ defaultMessage: "Select a currency" })}
                displayValue={(c) => c?.name ?? ""}
                onKeyDown={(e) => {
                  if (e.key !== "Tab" && e.key !== "Enter") {
                    e.preventDefault();
                  }
                }}
                required
              />
              <ComboboxButton className="combobox--button group">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="size-4"
                >
                  <path d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" />
                </svg>
              </ComboboxButton>
              {isPending && (
                <div className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2">
                  <Icon name="loading" className="animate-spin" />
                </div>
              )}
            </div>
            <ComboboxOptions anchor="bottom" className="combobox--options">
              {currencyList.map((currency) => (
                <ComboboxOption
                  key={currency.stringId}
                  value={currency}
                  className="combobox--option"
                >
                  <div className="text-sm/6">{currency.name}</div>
                </ComboboxOption>
              ))}
            </ComboboxOptions>
          </Combobox>
        </div>
      </div>
    </div>
  );
}

export function CryptoWalletBlock() {
  const { $t } = useTranslate();
  const walletAddressRef = useRef<HTMLInputElement>(null);
  const confirmAddressRef = useRef<HTMLInputElement>(null);

  function sanitizeInput(value: string): string {
    return value.replace(/[^a-zA-Z0-9]/g, "");
  }

  function handleInput(e: FormEvent<HTMLInputElement>) {
    const input = e.currentTarget;
    const sanitized = sanitizeInput(input.value);
    if (input.value !== sanitized) {
      input.value = sanitized;
    }
    validateMatch(walletAddressRef, confirmAddressRef);
  }

  function validateMatch(
    addressRef: RefObject<HTMLInputElement | null>,
    confirmRef: RefObject<HTMLInputElement | null>,
  ) {
    if (!confirmRef.current) return;
    const mismatch = addressRef.current?.value !== confirmRef.current.value;
    confirmRef.current.setCustomValidity(
      mismatch ? $t({ defaultMessage: "Wallet addresses do not match" }) : "",
    );
  }

  return (
    <div className="billing-information-block space-y-4">
      <div>
        <label htmlFor="crypto-wallet-address">
          {$t({ defaultMessage: "Wallet Address" })}
          {" *"}
        </label>
        <input
          id="crypto-wallet-address"
          ref={walletAddressRef}
          type="text"
          name="payment-account"
          placeholder={$t({ defaultMessage: "Wallet Address" })}
          minLength={26}
          maxLength={64}
          pattern="[a-zA-Z0-9]{26,64}"
          title={$t({
            defaultMessage:
              "26-64 characters, letters and numbers only, no spaces or special characters",
          })}
          required
          className="input"
          onInput={handleInput}
        />
      </div>
      <div>
        <label htmlFor="crypto-confirm-wallet-address">
          {$t({ defaultMessage: "Confirm Wallet Address" })}
          {" *"}
        </label>
        <input
          id="crypto-confirm-wallet-address"
          ref={confirmAddressRef}
          type="text"
          name="confirm-payment-account"
          placeholder={$t({ defaultMessage: "Confirm Wallet Address" })}
          minLength={26}
          maxLength={64}
          pattern="[a-zA-Z0-9]{26,64}"
          title={$t({
            defaultMessage:
              "26-64 characters, letters and numbers only, no spaces or special characters",
          })}
          required
          className="input"
          onInput={handleInput}
        />
      </div>
    </div>
  );
}

export function PhoneNumberField() {
  const { $t } = useTranslate();

  return (
    <>
      <span>{$t({ defaultMessage: "Enter phone number" })}</span>
      <div className="max-w-[28.125rem]">
        <label className="block font-bold">
          {$t({ defaultMessage: "Phone number" })}
          {" *"}
        </label>
        <input
          type="tel"
          name="payment-account"
          placeholder={$t({ defaultMessage: "Phone number" })}
          autoComplete="tel"
          required
          pattern="[0-9]{6,100}"
          className="input w-full"
        />
      </div>
    </>
  );
}

export function CardNumberField() {
  const { $t } = useTranslate();

  return (
    <>
      <span>{$t({ defaultMessage: "Enter card number" })}</span>
      <div className="max-w-[28.125rem]">
        <label className="block font-bold">
          {$t({ defaultMessage: "Card number" })}
          {" *"}
        </label>
        <input
          type="tel"
          name="payment-account"
          placeholder="xxxx xxxx xxxx xxxx"
          autoComplete="cc-number"
          inputMode="numeric"
          pattern="[0-9\s]{13,19}"
          required
          maxLength={19}
          className="input w-full"
        />
      </div>
    </>
  );
}

export function PixKeyField() {
  const { $t } = useTranslate();

  return (
    <>
      <span>{$t({ defaultMessage: "Enter PIX key" })}</span>
      <div className="max-w-[28.125rem]">
        <label className="block font-bold">PIX key *</label>
        <input
          type="text"
          name="payment-account"
          placeholder="PIX key"
          minLength={3}
          maxLength={100}
          required
          className="w-full"
        />
      </div>
    </>
  );
}

export function PayPalAddressField() {
  const { $t } = useTranslate();

  return (
    <>
      <span>{$t({ defaultMessage: "Enter PayPal address" })}</span>
      <div className="max-w-[28.125rem]">
        <label className="block font-bold">
          {$t({ defaultMessage: "PayPal address" })}
          {" *"}
        </label>
        <input
          type="text"
          name="payment-account"
          placeholder={$t({ defaultMessage: "PayPal address" })}
          minLength={3}
          maxLength={255}
          required
          className="w-full"
        />
      </div>
    </>
  );
}

interface IPaymentMethodCardProps {
  id: number;
  name: string;
  logoUrl: string | null;
  commission?: string | null;
  limits?: {
    min: number;
    max: number;
  };
}

export function PaymentMethodCard({
  id,
  name,
  logoUrl,
  commission,
  limits,
}: IPaymentMethodCardProps) {
  const { $t } = useTranslate();
  const formatMoney = usePlayerMoneyFormatter();

  return (
    <Radio as="div" value={id} className="payment-method-card">
      <div className="relative grow">
        {logoUrl ? (
          <img
            className="absolute size-full object-contain px-3.5 pt-2.5 pb-5"
            src={logoUrl}
            alt={name}
          />
        ) : null}
        {commission && Number(commission) > 0 && (
          <div
            style={{
              color: "var(--text-secondary-color)",
            }}
            className="absolute right-0 bottom-1 left-0 px-2.5 text-center font-medium text-10/none uppercase"
          >
            {$t({ defaultMessage: "commission: {commission}%" }, { commission })}
          </div>
        )}
      </div>

      {!isNil(limits) && (
        <div
          style={{
            background: "var(--bg-level-0)",
          }}
          className="flex-center whitespace-nowrap px-2.5 py-1.5 text-center text-14/none"
        >
          {formatMoney(limits.min ?? 0, { style: "decimal" })}
          {" - "}
          {formatMoney(limits.max ?? 0)}
        </div>
      )}
    </Radio>
  );
}

export function PIXBillingInformation() {
  const { $t } = useTranslate();

  return (
    <div className="billing-information-block space-y-4">
      <div>
        <label>
          {$t({ defaultMessage: "Phone number" })}
          {" *"}
        </label>
        <input
          type="tel"
          name="billing-pix-mobile"
          placeholder="+79991234567"
          autoComplete="tel"
          minLength={2}
          maxLength={13}
          required
          pattern="[0-9\+]{2,13}"
          title={$t({
            defaultMessage: "Only numbers and + sign are allowed",
          })}
          className="input"
        />
      </div>
      <div>
        <label>
          {$t({ defaultMessage: "Email" })}
          {" *"}
        </label>
        <input
          type="email"
          name="billing-pix-email"
          placeholder="test@gmail.com"
          autoComplete="email"
          pattern="[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}"
          title={$t({
            defaultMessage: "Please enter a valid email address",
          })}
          required
          className="input"
        />
      </div>
      <div>
        <label>
          {$t({ defaultMessage: "Tax number" })}
          {" *"}
        </label>
        <input
          type="text"
          name="billing-pix-tax-number"
          placeholder="123412341234"
          maxLength={20}
          required
          className="input"
        />
      </div>
    </div>
  );
}

export function PayPalBillingInformation() {
  const { $t } = useTranslate();

  return (
    <div className="billing-information-block space-y-4">
      <div className="flex gap-2 *:flex-1">
        <div>
          <label>
            {$t({ defaultMessage: "First name" })}
            {" *"}
          </label>
          <input
            type="text"
            name="billing-first-name"
            placeholder={$t({ defaultMessage: "First name" })}
            autoComplete="given-name"
            minLength={2}
            maxLength={100}
            required
            pattern="\D{2,100}"
            title={$t({
              defaultMessage: "2 or more characters. Numbers are not allowed",
            })}
            className="input"
          />
        </div>
        <div>
          <label>
            {$t({ defaultMessage: "Last name" })}
            {" *"}
          </label>
          <input
            type="text"
            name="billing-last-name"
            placeholder={$t({ defaultMessage: "Last name" })}
            autoComplete="family-name"
            minLength={2}
            maxLength={100}
            required
            pattern="\D{2,100}"
            title={$t({
              defaultMessage: "2 or more characters. Numbers are not allowed",
            })}
            className="input"
          />
        </div>
      </div>
    </div>
  );
}
